import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    await fetch('https://readdy.ai/api/form/d79n7bbll6jbq2vddq00', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    });
    setSubmitted(true);
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Florida%20coastline%20with%20luxury%20hotel%20resort%20buildings%20along%20the%20beach%2C%20warm%20golden%20sunrise%20light%2C%20turquoise%20ocean%20water%2C%20palm%20trees%2C%20upscale%20hospitality%20architecture%2C%20professional%20real%20estate%20photography%2C%20cinematic%20wide%20angle%2C%20rich%20warm%20tones%2C%20clear%20sky%2C%20premium%20resort%20destination&width=1920&height=1080&seq=hero2025a&orientation=landscape"
          alt="Florida Hotel Management"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-32 pt-40">
        <div className="max-w-3xl">
          {/* Pre-headline badge */}
          <div className={`inline-flex items-center space-x-2 mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="h-px w-8 bg-amber-400"></div>
            <span className="text-xs font-medium text-amber-400 uppercase tracking-[0.25em]">
              Florida&apos;s Performance-First Hotel Management Partner
            </span>
          </div>

          {/* Headline */}
          <div className={`mb-8 transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h1 className="text-6xl lg:text-7xl xl:text-8xl font-light text-white leading-[1.05] tracking-tight">
              Your Hotel
              <br />
              <span className="text-amber-400 italic">Performs.</span>
            </h1>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-extralight text-white/80 leading-tight mt-3">
              Or We Don&apos;t Get Paid.
            </h2>
          </div>

          {/* Subheadline */}
          <p className={`text-lg text-gray-300 font-light leading-relaxed max-w-2xl mb-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            360 Hospitality Group is Florida&apos;s only hotel management partner with performance-based fees. We handle operations, revenue, asset strategy, and contract advisory — and our compensation is tied to your results. Not the other way around.
          </p>

          {/* CTAs */}
          <div className={`flex flex-wrap items-start gap-3 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <button
              onClick={() => setShowForm(true)}
              className="group px-6 py-3 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center space-x-2"
            >
              <span>Get a Free Assessment</span>
              <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1"></i>
            </button>
            <Link
              to="/services"
              className="group px-6 py-3 rounded-full border border-white/30 text-white text-sm font-light tracking-wide hover:border-amber-400/60 hover:bg-white/5 transition-all duration-300 whitespace-nowrap flex items-center space-x-2"
            >
              <span>See How It Works</span>
              <i className="ri-arrow-right-line text-base text-amber-400 transition-transform group-hover:translate-x-1"></i>
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-white/50 rounded-full"></div>
        </div>
      </div>

      {/* Assessment Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-8 relative">
            <button
              onClick={() => { setShowForm(false); setSubmitted(false); }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-2">Request Received</h3>
                <p className="text-gray-500 font-light">We&apos;ll be in touch within 24 hours to schedule your free performance assessment.</p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-light text-gray-900 mb-1">Free Performance Assessment</h3>
                <p className="text-sm text-gray-500 font-light mb-6">Tell us about your property and we&apos;ll reach out within 24 hours.</p>
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">First Name</label>
                      <input name="first_name" required type="text" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="John" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Last Name</label>
                      <input name="last_name" required type="text" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="Smith" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Email</label>
                    <input name="email" required type="email" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="john@yourhotel.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Phone</label>
                    <input name="phone" type="tel" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="(321) 000-0000" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Property Name &amp; Location</label>
                    <input name="property" required type="text" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="e.g. Sunset Inn, Orlando FL" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Number of Rooms</label>
                    <input name="rooms" type="number" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="e.g. 85" />
                  </div>
                  <button type="submit" className="w-full py-3 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white font-medium text-sm tracking-wide transition-all whitespace-nowrap cursor-pointer">
                    Submit Request
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
